'use client';

/**
 * One GET, its error, and optionally a timer.
 *
 * Fifteen screens had written out the same eleven lines, and not identically:
 * some blanked the table on a failed refetch, one never cleared its interval,
 * none skipped a tick while a request was still in flight, all polled hidden
 * tabs. Decided once here:
 *
 * - stale data survives a failed refetch; the error shows above it
 * - `null` means "not yet", not "empty", so a spinner and an empty state differ
 * - a timer tick mid-flight is skipped; `reload()` after an edit never is
 * - nothing is set after unmount, and a hidden tab does not poll
 */


import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { PanelTable } from '@content-removal/shared';
import { useApi } from './api-context';
import { usePanelStream } from './panel-stream';

export interface Resource<T> {
  /** `null` until the first answer arrives; the last good value after that. */
  data: T | null;
  /** The most recent failure, or null. Cleared by the next success. */
  error: string | null;
  /** True while a request is in flight, first load included. */
  loading: boolean;
  /**
   * Has the first answer arrived — success or failure?
   *
   * `data === null` is two different situations and a screen that cannot tell
   * them apart shows an empty state for a list nobody has fetched yet: the
   * Cases queue said "No clients are assigned to you yet" for the two seconds
   * its own request was in flight, which is not a slow screen but a wrong
   * one. Render a {@link Waiting} shape until this is true, and the empty
   * state only after.
   */
  settled: boolean;

  /** Fetch now, off a button or after a mutation. Never throws. */
  /** Reads again and hands back what came, so a caller can say what changed. */
  reload: () => Promise<T | null>;
  /**
   * Write the value locally, without a request.
   *
   * For the optimistic update only: a row is removed or a chip flipped the
   * instant the person clicks, so the screen answers immediately instead of
   * waiting out a round trip. The next `reload` — the button's own, or a poll
   * tick — replaces whatever this wrote with the truth, which is the point:
   * an optimistic write that the server refused is corrected within a tick
   * rather than persisting as a lie until the page is reopened.
   */
  setData: Dispatch<SetStateAction<T | null>>;
}

export interface ResourceOptions<T = unknown> {
  /**
   * Read again when this collection is announced as changed.
   *
   * Not a timer. Ten screens used to ask their endpoint every twenty seconds
   * whether anything had happened, and the answer was almost always no —
   * twenty open tabs meant a request a second against a pool this box shares
   * with the scanners, all day, to learn nothing. The end that made the change
   * already knows; it says so on the panel's stream (`PANEL_TABLE`), and this
   * re-reads then.
   *
   * The stream coalesces a burst into one wake-up and stops while the tab is
   * hidden — see `usePanelStream`, which has done this for the case board
   * since the board stopped lying all morning.
   */
  live?: PanelTable;
  /** Hold off until true — for a screen whose path depends on a prior read. */
  enabled?: boolean;
  /**
   * What the server already fetched for this very render.
   *
   * Not the cached first paint the note below refuses, and the difference is
   * the whole point: that was a previous visit's answer replayed out of
   * storage, figures nobody had just asked for, on screens people read to
   * decide what to send to a hosting company. This is an answer fetched
   * moments ago, on the server, as part of producing this page — the same
   * request, one hop earlier. It costs the browser a round trip and a spinner
   * and shows nothing that was not current when the page was built.
   *
   * A poll or a `reload()` replaces it exactly as it would replace a value
   * fetched here.
   */
  initial?: T | null;
}

/**
 * Deliberately no cached first paint.
 *
 * A previous answer rendered instantly from `sessionStorage` removes the wait
 * on a refresh, and it was tried here — but it also means the screen shows
 * figures nobody has just fetched, and this product's screens are read to
 * decide what to send to a hosting company. One state for waiting, everywhere,
 * and it is the shapes: a reader learns one thing rather than two, and never
 * has to wonder whether what they are looking at is current.
 */
export function useResource<T>(path: string | null, options: ResourceOptions<T> = {}): Resource<T> {
  const { live, enabled = true, initial = null } = options;
  const api = useApi();
  const [data, setData] = useState<T | null>(initial);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Handed a server-rendered answer, this screen is not waiting for anything:
  // an empty list is empty, and says so, rather than showing waiting shapes
  // over data that is already on the page.
  const [settled, setSettled] = useState(initial !== null);

  const alive = useRef(true);
  /* What is in flight, and what it asks for. The path matters: a tick that
     skipped because *something* was in flight left the screen holding an
     answer to the previous question — the case board asked for six columns
     while a three-column read was open, the skip dropped it, and three
     columns sat on "Loading…" until the stream happened to wake them. */
  const inFlight = useRef<{ path: string; run: Promise<T | null> } | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /* Hands back what it read as well as putting it in state: a caller that has
     just changed something on the server ("refresh the WHOIS") has to be able
     to say what came back, and reading `data` straight after is reading the
     render before this one. */
  const fetchOnce = useCallback(async (): Promise<T | null> => {
    if (!path) return null;
    setLoading(true);
    try {
      const next = await api<T>(path);
      if (!alive.current) return next;
      setData(next);
      setError(null);
      return next;
    } catch (err) {
      if (!alive.current) return null;
      // `data` is deliberately left alone: stale rows beat a blank screen.
      setError((err as Error).message);
      return null;
    } finally {
      if (alive.current) {
        setLoading(false);
        // Settled means answered, either way: a screen waiting on this must
        // stop waiting on a failure too, or a dead endpoint shows shapes
        // forever instead of the error above them.
        setSettled(true);
      }
    }
  }, [path, api]);

  /** Reads, once whatever is in flight has finished. */
  const read = useCallback(async (): Promise<T | null> => {
    if (!path || !enabled) return null;
    const pending = inFlight.current;
    if (pending) await pending.run.catch(() => undefined);
    /* Cleared only by the read that set it: two reads waiting on the same
       pending one would otherwise have the first clear the second's entry,
       and the skip above would then let a duplicate through. */
    const mine: { path: string; run: Promise<T | null> } = { path, run: Promise.resolve(null) };
    mine.run = fetchOnce().finally(() => {
      if (inFlight.current === mine) inFlight.current = null;
    });
    inFlight.current = mine;
    return mine.run;
  }, [path, enabled, fetchOnce]);

  /**
   * Skipped only while this very question is in flight: a burst is one read,
   * not five, and a read of something else is not this one's answer.
   */
  const poll = useCallback(async () => {
    if (!path || !enabled) return;
    if (inFlight.current?.path === path) return;
    await read();
  }, [path, enabled, read]);

  /** Never skipped: waits out whatever is in flight, then reads again. */
  const reload = read;

  // The first read. `initial` covers the frame before this: a screen handed
  // its rows by the server still asks once here, because the page may have
  // been rendered a moment before the browser got to it.
  useEffect(() => {
    if (!path || !enabled) return;
    void poll();
  }, [poll, path, enabled]);

  // And every read after it, when the API says this collection moved.
  usePanelStream(
    () => {
      void poll();
    },
    Boolean(live) && enabled && Boolean(path),
    live,
  );

  return { data, error, loading, settled, reload, setData };
}
